import React from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';
import poweredByUniledLogo from 'public/poweredByUniledLogo.png';
import ChartCard from './ChartCard';

type RunningTotalsDatum = { label: string; Total: number; Expected: number };

type LineRunningTotalsConfig = {
    kind: 'lineRunningTotals';
    title: string;
    data: RunningTotalsDatum[]; // e.g. rtImpacts / rtPlays
    triplet?: {
        pct: number | null; // e.g. histImpactsPct
        target: number | undefined; // e.g. impactsExpected
        total: number | undefined; // e.g. impactsActual
        label: string; // 'Impacts' | 'Impressions' | 'Plays'
        targetLabel?: string; // default: `Target ${label}`
        totalLabel?: string; // default: `Total ${label}`
    };
};

type BarTotalVsExpectedConfig = {
    kind: 'barTotalVsExpected';
    title: string;
    data: any[]; // e.g. owners
    xKey: string; // 'name'
    totalKey: string; // 'impacts_total' / 'plays_total'
    expectedKey: string; // 'impacts_expected' / 'plays_expected'
    names: { total: string; expected: string }; // legend labels
};

type ReportHeaderProps = {
    brand: string;
    logoUrl: string;
    mb?: number;
    showBrandBar?: boolean;
};

type TwoChartPageProps = {
    mainTitle: string;
    brand: string;
    logoUrl: string;
    footer: { title: string; campaignId: number | string; brand: string };
    left: LineRunningTotalsConfig | BarTotalVsExpectedConfig;
    right: LineRunningTotalsConfig | BarTotalVsExpectedConfig;
    showDivider?: boolean;
    showValueLabels?: boolean;
    valueLabelFontSize?: number;
    xAxisTickFontSize?: number;
    fmtCompact: (n?: number) => string; // use your existing helper
    fmtInt: (n?: number) => string; // "
    perfColor: (p: number | null) => string; // "
};

const ReportHeader: React.FC<ReportHeaderProps> = ({
    brand,
    logoUrl,
    mb = 3,
    showBrandBar = false,
}) => (
    <>
        {showBrandBar && <Box sx={{ height: 10, bgcolor: brand, borderRadius: 1, mb }} />}

        <Stack pl={3} pt={2} direction="row" alignItems="flex-end" gap={2} sx={{ width: 1, mb }}>
            <Box
                component="img"
                src={logoUrl}
                alt="Logo"
                className="header-logo"
                sx={{ maxHeight: 48, maxWidth: 270, objectFit: 'contain', display: 'block' }}
            />
        </Stack>
    </>
);

const ReportFooter = ({
    title,
    campaignId,
    brand,
}: {
    title: string;
    campaignId: number | string;
    brand: string;
}) => (
    <Box>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={-3}
            sx={{ px: 2, py: 0.5 }}
        >
            <Typography sx={{ fontWeight: 800 }} pl={5}>
                {title} | # {campaignId}
            </Typography>
            <Box
                pr={2}
                component="img"
                src={poweredByUniledLogo}
                alt="Powered by UniLED"
                sx={{ maxHeight: 48, maxWidth: 270, objectFit: 'contain', display: 'block' }}
            />
        </Box>
        <Box sx={{ height: 40, bgcolor: brand }} mt={1.5} />
    </Box>
);

const TwoChartPage: React.FC<TwoChartPageProps> = ({
    mainTitle,
    brand,
    logoUrl,
    footer,
    left,
    right,
    showDivider = true,
    showValueLabels = false,
    valueLabelFontSize = 10,
    xAxisTickFontSize = 11,
    fmtCompact,
    fmtInt,
    perfColor,
}) => {
    const hasTriplet =
        (left.kind === 'lineRunningTotals' && !!left.triplet) ||
        (right.kind === 'lineRunningTotals' && !!right.triplet);
    const renderLeft = renderPane(left, {
        brand,
        fmtCompact,
        fmtInt,
        perfColor,
        showValueLabels,
        valueLabelFontSize,
        xAxisTickFontSize,
    });
    const renderRight = renderPane(right, {
        brand,
        fmtCompact,
        fmtInt,
        perfColor,
        showValueLabels,
        valueLabelFontSize,
        xAxisTickFontSize,
    });

    return (
        <Box className="page" sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
            <Box className="page-header">
                <ReportHeader brand={brand} logoUrl={logoUrl} />
            </Box>

            {showDivider && <Box className="center-divider" />}

            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.5 }}>
                {mainTitle}
            </Typography>

            <Grid container spacing={3} className="two-col" sx={{ paddingTop: hasTriplet ? 0 : 3 }}>
                <Grid item xs={12} md={6}>
                    {renderLeft}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderRight}
                </Grid>
            </Grid>

            <Box className="page-footer">
                <ReportFooter {...footer} />
            </Box>
        </Box>
    );
};

// --- helpers: wrap long labels at spaces (hard-break very long words) ---
// helpers: wrap at spaces, hard-break very long words
const wrapLabel = (v: unknown, max = 23): string[] => {
    const s = (v ?? '').toString();
    if (!s) return [''];
    const words = s.split(/\s+/);
    const lines: string[] = [];
    let line = '';

    const push = () => {
        if (line) {
            lines.push(line);
            line = '';
        }
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const w of words) {
        if (!line) {
            if (w.length <= max) line = w;
            else {
                const chunks = w.match(new RegExp(`.{1,${max}}`, 'g')) || [w];
                lines.push(...chunks.slice(0, -1));
                line = chunks[chunks.length - 1];
            }
            // eslint-disable-next-line no-continue
            continue;
        }
        if (`${line} ${w}`.length <= max) line += ` ${w}`;
        else if (w.length <= max) {
            push();
            line = w;
        } else {
            push();
            const chunks = w.match(new RegExp(`.{1,${max}}`, 'g')) || [w];
            lines.push(...chunks.slice(0, -1));
            line = chunks[chunks.length - 1];
        }
    }
    push();
    return lines;
};

const HIDDEN_REPORT_X_AXIS_ANGLE = -36;
const HIDDEN_REPORT_X_AXIS_HEIGHT = 78;
const HIDDEN_REPORT_X_AXIS_TICK_MARGIN = 5;
const HIDDEN_REPORT_SIDE_MARGIN = 20;
const HIDDEN_REPORT_BOTTOM_MARGIN = 18;

// angled, multi-line tick for Recharts
const AngledMultiLineTick: React.FC<{
    x?: number;
    y?: number;
    payload?: { value: any };
    max?: number;
    lineHeight?: number;
    fontSize?: number;
    angle?: number;
    anchor?: 'start' | 'middle' | 'end';
}> = ({
    x = 0,
    y = 0,
    payload,
    max = 23,
    lineHeight = 12,
    fontSize = 11,
    angle = -35,
    anchor = 'end',
}) => {
    const lines = wrapLabel(payload?.value, max);
    return (
        <g transform={`translate(${x},${y}) rotate(${angle})`}>
            <text textAnchor={anchor} style={{ fontSize }}>
                {lines.map((ln, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight}>
                        {ln}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

const buildVisibleLineLabelIndexes = (
    data: RunningTotalsDatum[],
    dataKey: 'Total' | 'Expected',
    formatLabel: (n?: number) => string,
    approxPlotWidth = 460,
) => {
    const count = data.length;
    const indexes = new Set<number>();

    if (!count) return indexes;
    if (count === 1) {
        indexes.add(0);
        return indexes;
    }

    const values = data.map(d => (dataKey === 'Expected' ? d.Expected : d.Total));
    const maxLabelChars = Math.max(...values.map(v => formatLabel(v).length), 1);

    const estimatedLabelWidthPx = Math.max(42, Math.min(84, maxLabelChars * 6 + 10));
    const pointSpacingPx = approxPlotWidth / Math.max(count - 1, 1);

    const minIndexGap = Math.max(1, Math.ceil(estimatedLabelWidthPx / pointSpacingPx));
    const flatMinIndexGap = Math.max(
        minIndexGap,
        Math.ceil((estimatedLabelWidthPx + 8) / pointSpacingPx),
    );

    let lastShown = -999;

    for (let i = 0; i < count; i += 1) {
        const val = values[i];
        const prev = i > 0 ? values[i - 1] : undefined;
        const next = i < count - 1 ? values[i + 1] : undefined;

        const isFirst = i === 0;
        const isLast = i === count - 1;

        const startsFlatRun = next === val && prev !== val;
        const insideFlatRun = prev === val || next === val;
        const endsFlatRun = prev === val && next !== val;

        const gapNeeded = insideFlatRun ? flatMinIndexGap : minIndexGap;
        const farEnough = i - lastShown >= gapNeeded;

        const shouldShow =
            isFirst || isLast || endsFlatRun || (startsFlatRun && farEnough) || farEnough;

        if (shouldShow) {
            indexes.add(i);
            lastShown = i;
        }
    }

    const arr = [...indexes].sort((a, b) => a - b);
    if (arr.length >= 2) {
        const last = arr[arr.length - 1];
        const prev = arr[arr.length - 2];
        if (last - prev < minIndexGap) {
            indexes.delete(prev);
        }
    }

    return indexes;
};

const suppressCrossSeriesLabelCollisions = (
    data: RunningTotalsDatum[],
    totalIndexes: Set<number>,
    expectedIndexes: Set<number>,
) => {
    const adjustedExpected = new Set(expectedIndexes);

    if (!data.length) {
        return {
            totalIndexes,
            expectedIndexes: adjustedExpected,
        };
    }

    const maxValue = Math.max(...data.map(d => Math.max(d.Total ?? 0, d.Expected ?? 0)), 1);

    data.forEach((row, index) => {
        const totalShown = totalIndexes.has(index);
        const expectedShown = adjustedExpected.has(index);

        if (!totalShown || !expectedShown) return;

        const delta = Math.abs((row.Total ?? 0) - (row.Expected ?? 0));

        // If both series would label the same x position and are visually close,
        // keep Total and suppress Expected.
        if (delta < maxValue * 0.04) {
            adjustedExpected.delete(index);
        }
    });

    return {
        totalIndexes,
        expectedIndexes: adjustedExpected,
    };
};

const LineValueLabel: React.FC<{
    x?: number | string;
    y?: number | string;
    value?: number | string;
    index?: number;
    dataKey?: 'Total' | 'Expected';
    payload?: RunningTotalsDatum;
    visibleIndexes: Set<number>;
    data: RunningTotalsDatum[];
    color: string;
    formatLabel: (n?: number) => string;
    fontSize: number;
    viewBox?: any;
}> = ({
    x = 0,
    y = 0,
    value,
    index = 0,
    dataKey,
    payload,
    visibleIndexes,
    data,
    color,
    formatLabel,
    fontSize,
    viewBox,
}) => {
    if (!visibleIndexes.has(index)) return null;

    const anchorX = typeof x === 'number' ? x : Number(x) || 0;
    const baseY = typeof y === 'number' ? y : Number(y) || 0;
    const ownValue =
        typeof value === 'number' ? value : typeof value === 'string' ? Number(value) || 0 : 0;
    const row = data[index] ?? payload;
    const expectedValue = row?.Expected ?? 0;
    const totalValue = row?.Total ?? 0;
    const isExpected = dataKey === 'Expected';
    const expectedAbove = expectedValue > totalValue;
    const yOffset = isExpected ? (expectedAbove ? -14 : 14) : expectedAbove ? 14 : -14;
    const anchorY =
        typeof viewBox?.y === 'number'
            ? viewBox.y
            : typeof viewBox?.y === 'string'
            ? Number(viewBox.y) || baseY
            : baseY;
    const xOffset = index === 0 ? 10 : 0;
    const textAnchor = index === 0 ? 'start' : 'middle';
    const dominantBaseline = yOffset > 0 ? 'hanging' : 'auto';

    return (
        <text
            x={anchorX + xOffset}
            y={anchorY + yOffset}
            textAnchor={textAnchor}
            dominantBaseline={dominantBaseline}
            style={{ fontSize, fill: color }}
        >
            {formatLabel(ownValue)}
        </text>
    );
};

/** internal: renders one pane based on its config */
function renderPane(
    cfg: LineRunningTotalsConfig | BarTotalVsExpectedConfig,
    utils: {
        brand: string;
        fmtCompact: (n?: number) => string;
        fmtInt: (n?: number) => string;
        perfColor: (p: number | null) => string;
        showValueLabels: boolean;
        valueLabelFontSize: number;
        xAxisTickFontSize: number;
    },
) {
    const {
        brand,
        fmtCompact,
        fmtInt,
        perfColor,
        showValueLabels,
        valueLabelFontSize,
        xAxisTickFontSize,
    } = utils;
    const formatDisplayValue = (value?: number) =>
        Number(value || 0).toLocaleString(undefined, {
            notation: 'compact',
            maximumFractionDigits: 1,
        });

    if (cfg.kind === 'lineRunningTotals') {
        const { data, title, triplet } = cfg;
        const rawTotalVisibleIndexes = buildVisibleLineLabelIndexes(
            data,
            'Total',
            formatDisplayValue,
        );
        const rawExpectedVisibleIndexes = buildVisibleLineLabelIndexes(
            data,
            'Expected',
            formatDisplayValue,
        );

        const { totalIndexes: totalVisibleIndexes, expectedIndexes: expectedVisibleIndexes } =
            suppressCrossSeriesLabelCollisions(
                data,
                rawTotalVisibleIndexes,
                rawExpectedVisibleIndexes,
            );

        return (
            <ChartCard title={title} brand={brand}>
                {data?.length ? (
                    <ResponsiveContainer width="100%" height="91%">
                        <LineChart
                            data={data}
                            margin={{
                                top: 16,
                                right: HIDDEN_REPORT_SIDE_MARGIN + 12,
                                left: HIDDEN_REPORT_SIDE_MARGIN,
                                bottom: HIDDEN_REPORT_BOTTOM_MARGIN,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: xAxisTickFontSize }}
                                angle={HIDDEN_REPORT_X_AXIS_ANGLE}
                                textAnchor="end"
                                height={HIDDEN_REPORT_X_AXIS_HEIGHT}
                                tickMargin={HIDDEN_REPORT_X_AXIS_TICK_MARGIN}
                            />
                            <YAxis tickFormatter={fmtCompact} width={44} tick={{ fontSize: 12 }} />
                            <Legend
                                verticalAlign="top"
                                align="right"
                                iconSize={10}
                                wrapperStyle={{ fontSize: 11, lineHeight: '14px' }}
                                formatter={value => <span style={{ color: '#000' }}>{value}</span>}
                            />
                            <Line
                                name={`Total ${triplet?.label ?? ''}`}
                                type="monotone"
                                dataKey="Total"
                                stroke={brand}
                                strokeWidth={3}
                                dot={false}
                            >
                                {showValueLabels && (
                                    <LabelList
                                        dataKey="Total"
                                        content={props => (
                                            <LineValueLabel
                                                {...props}
                                                dataKey="Total"
                                                data={data}
                                                visibleIndexes={totalVisibleIndexes}
                                                color={brand}
                                                formatLabel={formatDisplayValue}
                                                fontSize={valueLabelFontSize}
                                            />
                                        )}
                                    />
                                )}
                            </Line>
                            <Line
                                name={`Expected ${triplet?.label ?? ''}`}
                                type="monotone"
                                dataKey="Expected"
                                stroke="#bdbdbd"
                                strokeWidth={3}
                                strokeDasharray="5 6"
                                dot={false}
                            >
                                {showValueLabels && (
                                    <LabelList
                                        dataKey="Expected"
                                        content={props => (
                                            <LineValueLabel
                                                {...props}
                                                dataKey="Expected"
                                                data={data}
                                                visibleIndexes={expectedVisibleIndexes}
                                                color="#bdbdbd"
                                                formatLabel={formatDisplayValue}
                                                fontSize={valueLabelFontSize}
                                            />
                                        )}
                                    />
                                )}
                            </Line>
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Box
                        sx={{
                            height: 380,
                            display: 'grid',
                            placeItems: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        No data
                    </Box>
                )}

                {triplet && (
                    <Grid
                        container
                        spacing={2}
                        pl={5}
                        pr={5}
                        pt={2}
                        className="triplet"
                        alignItems="stretch"
                        sx={{ '@media print': { minHeight: 120 } }}
                    >
                        <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{
                                '@media print': {
                                    width: '33.3333%',
                                    flexBasis: '33.3333%',
                                    maxWidth: '33.3333%',
                                },
                            }}
                        >
                            <Box
                                className="stat-card"
                                sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'grey',
                                    height: '70%',
                                    bgcolor: '#fff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center', // ⟵ center horizontally
                                    justifyContent: 'center', // ⟵ center vertically
                                    textAlign: 'center',
                                    gap: 0.25,
                                }}
                            >
                                <Typography
                                    sx={{ fontWeight: 800, color: perfColor(triplet.pct ?? null) }}
                                >
                                    {triplet.pct == null ? '-' : `${triplet.pct.toFixed(2)}%`}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {triplet.label}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{
                                '@media print': {
                                    width: '33.3333%',
                                    flexBasis: '33.3333%',
                                    maxWidth: '33.3333%',
                                },
                            }}
                        >
                            <Box
                                className="stat-card"
                                sx={{
                                    p: 1.25,
                                    border: '1px solid',
                                    borderColor: 'grey',
                                    borderRadius: 2,
                                    height: '70%',
                                    bgcolor: '#fff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    gap: 0.25,
                                }}
                            >
                                <Typography sx={{ fontWeight: 800 }}>
                                    {formatDisplayValue(triplet.target)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                                >
                                    {triplet.targetLabel ?? `Target ${triplet.label}`}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{
                                '@media print': {
                                    width: '33.3333%',
                                    flexBasis: '33.3333%',
                                    maxWidth: '33.3333%',
                                },
                            }}
                        >
                            <Box
                                className="stat-card"
                                sx={{
                                    p: 1.25,
                                    border: '1px solid',
                                    borderColor: 'grey',
                                    borderRadius: 2,
                                    height: '70%',
                                    bgcolor: '#fff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    gap: 0.25,
                                }}
                            >
                                <Typography sx={{ fontWeight: 800 }}>
                                    {formatDisplayValue(triplet.total)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {triplet.totalLabel ?? `Total ${triplet.label}`}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </ChartCard>
        );
    }

    // barTotalVsExpected
    const { data, title, xKey, totalKey, expectedKey, names } = cfg;

    return (
        <ChartCard title={title} brand={brand}>
            {data?.length ? (
                <ResponsiveContainer width="100%" height="91%">
                    <BarChart
                        data={data}
                        margin={{
                            top: 8,
                            right: HIDDEN_REPORT_SIDE_MARGIN,
                            left: HIDDEN_REPORT_SIDE_MARGIN,
                            bottom: HIDDEN_REPORT_BOTTOM_MARGIN,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey={xKey}
                            angle={HIDDEN_REPORT_X_AXIS_ANGLE}
                            textAnchor="end"
                            height={HIDDEN_REPORT_X_AXIS_HEIGHT}
                            interval={0}
                            tickMargin={HIDDEN_REPORT_X_AXIS_TICK_MARGIN}
                            tick={
                                <AngledMultiLineTick
                                    max={23}
                                    lineHeight={12}
                                    fontSize={xAxisTickFontSize}
                                    angle={HIDDEN_REPORT_X_AXIS_ANGLE}
                                    anchor="end"
                                />
                            }
                        />
                        <YAxis tickFormatter={fmtCompact} width={44} tick={{ fontSize: 12 }} />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            iconSize={10}
                            wrapperStyle={{ fontSize: 11, lineHeight: '14px' }}
                            formatter={value => <span style={{ color: '#000' }}>{value}</span>}
                        />
                        <Bar dataKey={expectedKey} name={names.expected} fill="#bdbdbd">
                            {showValueLabels && (
                                <LabelList
                                    dataKey={expectedKey}
                                    position="top"
                                    formatter={(value: number) => formatDisplayValue(value)}
                                    style={{ fontSize: valueLabelFontSize, fill: '#bdbdbd' }}
                                />
                            )}
                        </Bar>
                        <Bar dataKey={totalKey} name={names.total} fill={brand}>
                            {showValueLabels && (
                                <LabelList
                                    dataKey={totalKey}
                                    position="top"
                                    formatter={(value: number) => formatDisplayValue(value)}
                                    style={{ fontSize: valueLabelFontSize, fill: brand }}
                                />
                            )}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <Box
                    sx={{
                        height: 380,
                        display: 'grid',
                        placeItems: 'center',
                        color: 'text.secondary',
                    }}
                >
                    No data
                </Box>
            )}
        </ChartCard>
    );
}
export default TwoChartPage;
